import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { UseQueryResult } from "@tanstack/react-query";
import { parseAsString, parseAsStringEnum, useQueryStates } from "nuqs";
import { useAuthContext } from "providers/Auth";
import { useApplicationSummary } from "shared/hooks/use-application-summary";
import { useApplicationDecision } from "shared/hooks/use-application-decision";
import { useApplicantDetail } from "shared/hooks/use-applicant-detail";
import { VerificationNames } from "shared/types";
import { Application } from "types/application";

export const NON_VERIFICATION_REPORT_VALUES = [
  "additional_documents",
  "applicant"
] as const;

const REPORT_VALUES: string[] = [
  ...Object.values(VerificationNames),
  ...NON_VERIFICATION_REPORT_VALUES
];

export const useApplicantReportState = () => {
  const { applicationId } = useParams();
  const { permissions } = useAuthContext();

  const [rawParams, setRawParams] = useQueryStates(
    {
      applicantId: parseAsString.withDefault(""),
      report: parseAsStringEnum(REPORT_VALUES).withDefault("applicant")
    },
    { history: "replace" }
  );

  const queryKey = ["APPLICATION", String(applicationId)];

  const applicationMeta = useApplicationSummary(
    applicationId
  ) as unknown as UseQueryResult<Application | undefined>;
  const applicationDecision = useApplicationDecision(applicationId);

  // Skip the report-data request for users who can't view reports.
  const applicantDetail = useApplicantDetail(
    applicationId,
    (permissions.canViewReports && rawParams.applicantId) || undefined
  );

  useEffect(() => {
    if (!applicationMeta.data) return;
    if (!rawParams.applicantId) return;

    const matchesRealApplicant = applicationMeta.data.applicants.some(
      (appl) => String(appl.id) === String(rawParams.applicantId)
    );
    if (matchesRealApplicant) return;

    const firstApplicantId = applicationMeta.data.applicants[0]?.id;
    if (firstApplicantId === undefined) return;

    setRawParams({ applicantId: String(firstApplicantId) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the fetched application data changes
  }, [applicationMeta.data]);

  const searchParams = {
    applicantId: rawParams.applicantId,
    report: rawParams.report
  };

  const setSearchParams = (next: { applicantId: string; report: string }) => {
    setRawParams(next);
  };

  return {
    applicationMeta,
    applicationDecision,
    applicantDetail,
    searchParams,
    applicationId,
    setSearchParams,
    queryKey
  };
};
